import React, { useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import styles from './ScoopDropzone.module.scss'

/**
 * ScoopDropzone - Reusable drag-and-drop file upload component
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} [props.accept] - Object defining accepted file types (see react-dropzone docs)
 * @param {number} [props.maxFiles=1] - Maximum number of files
 * @param {Function} [props.onDrop] - Callback when files are dropped
 * @param {Array} [props.acceptedFiles] - Array of accepted files to display
 * @param {boolean} [props.showFileList=true] - Whether to show the list of accepted files
 * @param {React.ReactNode} [props.icon] - Custom icon to display
 * @param {string} [props.hint] - Hint text (e.g., "CSV, XLSX, HTML or other")
 * @param {string} [props.mainText] - Main instructional text
 * @param {string} [props.linkText='click here to select'] - Text for the clickable link
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.compact=false] - Use compact variant with less padding
 * @param {Object} [props.dropzoneProps] - Additional props to pass to useDropzone
 * @param {Function} [props.getRootProps] - Optional external getRootProps from useDropzone (controlled mode)
 * @param {Function} [props.getInputProps] - Optional external getInputProps from useDropzone (controlled mode)
 * @param {boolean} [props.isFocused] - Optional external focused state (controlled mode)
 * @param {boolean} [props.isDragAccept] - Optional external drag accept state (controlled mode)
 * @param {boolean} [props.isDragReject] - Optional external drag reject state (controlled mode)
 *
 * @example
 * // Automatic mode (component manages dropzone internally)
 * <ScoopDropzone
 *   accept={{ 'image/*': [] }}
 *   onDrop={(files) => setFiles(files)}
 *   hint="Only .PNG, .GIF, .SVG and .JPG files"
 * />
 *
 * @example
 * // Controlled mode (parent manages dropzone)
 * const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone()
 * <ScoopDropzone
 *   getRootProps={getRootProps}
 *   getInputProps={getInputProps}
 *   isFocused={isFocused}
 *   isDragAccept={isDragAccept}
 *   isDragReject={isDragReject}
 *   acceptedFiles={acceptedFiles}
 *   hint="Only .PNG, .GIF, .SVG and .JPG files"
 * />
 */
export const ScoopDropzone = ({
    accept,
    maxFiles = 1,
    onDrop,
    acceptedFiles = [],
    showFileList = true,
    icon,
    hint,
    mainText = "Drag 'n' drop your file here or",
    linkText = 'click here to select',
    className = '',
    compact = false,
    dropzoneProps = {},
    // Controlled mode props
    getRootProps: externalGetRootProps,
    getInputProps: externalGetInputProps,
    isFocused: externalIsFocused,
    isDragAccept: externalIsDragAccept,
    isDragReject: externalIsDragReject,
}) => {
    // Use internal dropzone only if external props are not provided
    const internalDropzone = useDropzone({
        accept,
        maxFiles,
        onDrop,
        ...dropzoneProps,
    })

    // Use external props if provided, otherwise use internal
    const getRootProps = externalGetRootProps || internalDropzone.getRootProps
    const getInputProps = externalGetInputProps || internalDropzone.getInputProps
    const isFocused =
        externalIsFocused !== undefined ? externalIsFocused : internalDropzone.isFocused
    const isDragAccept =
        externalIsDragAccept !== undefined ? externalIsDragAccept : internalDropzone.isDragAccept
    const isDragReject =
        externalIsDragReject !== undefined ? externalIsDragReject : internalDropzone.isDragReject

    const dropzoneClassName = useMemo(() => {
        const classes = [styles.dropzone]
        if (isFocused) classes.push(styles.focused)
        if (isDragAccept) classes.push(styles.accept)
        if (isDragReject) classes.push(styles.reject)
        if (compact) classes.push(styles.compact)
        if (className) classes.push(className)
        return classes.join(' ')
    }, [isFocused, isDragAccept, isDragReject, compact, className])

    const fileItems = acceptedFiles.map((file) => (
        <li key={file.path}>
            {file.path} - {file.size} bytes
        </li>
    ))

    return (
        <>
            <div
                {...getRootProps({
                    className: dropzoneClassName,
                    onMouseDown: (e) => e.stopPropagation(), // Prevent canvas drag selection
                })}
            >
                <input {...getInputProps()} />
                {icon && <div className={styles.icon}>{icon}</div>}
                {hint && <div className={styles.hint}>{hint}</div>}
                {mainText && <div className={styles.mainText}>{mainText}</div>}
                {linkText && <span className={styles.link}>{linkText}</span>}
            </div>
            {showFileList && acceptedFiles.length > 0 && (
                <ul className={styles.fileList}>{fileItems}</ul>
            )}
        </>
    )
}
